import { faSearch } from '@fortawesome/pro-light-svg-icons'
import { faIndustry, faUser } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch'
import { useQuery } from '@tanstack/react-query'
import 'instantsearch.css/themes/reset.css'
import 'instantsearch.css/themes/satellite.css'
import type { BaseHit, Hit, SearchClient } from 'instantsearch.js'
import {
  type PropsWithChildren,
  type ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  Configure,
  Highlight,
  InfiniteHits,
  InstantSearch,
  RefinementList,
  type RefinementListProps,
  SearchBox,
  useInstantSearch
} from 'react-instantsearch'
import { useUserSearchToken } from '~/api/queries/user'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { Modal } from '~/components/Global/Design/Modal/Modal'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { useConfig } from '~/domains/configuration/queries/config'
import { ApplicantModalContext } from '~/utils/contextes'
import { getApplicantModalGlobalRef } from '../Applicants/ApplicantModal/Global/applicantModalRef'
import {
  highlightRoot,
  hightlight,
  hitItem,
  hitList,
  itemIcon,
  itemIdentity,
  itemTitle,
  itemType,
  itemWrapper,
  keyStyle,
  keysWrapper,
  position,
  refinementCheckbox,
  refinementCount,
  refinementLabel,
  refinementLabelText,
  refinementList,
  refinementSeleted,
  searchBox,
  searchForm,
  searchInput,
  searchPlaceholder
} from './GlobalSearch.css'

interface HitProps extends BaseHit {
  id: string
  uid: string
  type: string
  fullname: string
  phone: string | null
  email: string
  company?: string
  registration_number?: string | null
}

const EmptyQueryBoundary = ({
  children
}: PropsWithChildren): ReactElement | null => {
  const { indexUiState } = useInstantSearch()

  if (indexUiState.query === '') {
    return null
  }

  return <>{children}</>
}

const NoResultsBoundary = ({
  children,
  fallback
}: PropsWithChildren<{ fallback: ReactElement }>): ReactElement => {
  const { results } = useInstantSearch()
  if (results.__isArtificial !== true && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    )
  }
  return children as ReactElement
}

const NoResults = (): ReactElement | null => {
  const { indexUiState } = useInstantSearch()
  const query = indexUiState.query?.trim()
  return (
    <NoResult
      isSmall
      message={`Aucun résultat${query !== '' && query !== undefined ? ` pour "${query}"` : ''}.`}
    />
  )
}

export const GlobalSearch = (): ReactElement => {
  const [open, setOpen] = useState(false)
  const { setApplicantCollection, setApplicantCurrentId } = useContext(
    ApplicantModalContext
  )

  const { data: searchEndpoint } = useConfig('organization.search_endpoint')
  const { data: slug } = useConfig('organization.slug')
  const { data: legacyUrl } = useConfig('organization.legacy_url')
  const { data: searchToken } = useUserSearchToken()
  const searchTokenValue = searchToken?.token

  const searchClient = useMemo(() => {
    if (
      !open ||
      searchEndpoint === undefined ||
      searchTokenValue === undefined
    ) {
      return null
    }

    const { searchClient: client } = instantMeiliSearch(
      searchEndpoint,
      searchTokenValue,
      { primaryKey: 'uid' }
    )

    return client
  }, [open, searchEndpoint, searchTokenValue])

  const healthCheck = useQuery({
    queryKey: ['globalsearch.healthcheck'],
    queryFn: async () => {
      if (searchEndpoint === undefined) {
        throw new Error('Brand data not found')
      }
      return await fetch(`${searchEndpoint}/health`).then(
        async (response) => await response.json()
      )
    },
    enabled: searchEndpoint !== undefined && searchToken !== undefined
  })

  const CustomHit = ({ hit }: { hit: Hit<HitProps> }): ReactElement => {
    const onClick = (): void => {
      let uri = ''
      if (hit.type === 'Candidat') {
        const applicantId = Number.parseInt(hit.id, 10)
        if (window.JobConfig !== undefined) {
          // Legacy
          getApplicantModalGlobalRef().current(applicantId, [applicantId])
        } else {
          setApplicantCollection([{ id: applicantId }])
          setApplicantCurrentId(applicantId)
        }
      }
      if (hit.type === 'Contact') {
        uri = `${legacyUrl ?? ''}/admin/contacts/modifier/${hit.id}`
      }

      if (uri !== '') {
        if (window.JobConfig?.instance.openNewTab === true) {
          window.open(uri, '_blank')
        } else {
          window.location.href = uri
        }
      }
    }
    return (
      <div onClick={onClick} key={hit.uid} className={itemWrapper}>
        <div className={itemIcon}>
          <FontAwesomeIcon
            icon={hit.type === 'Candidat' ? faUser : faIndustry}
            size="2x"
          />
        </div>
        <div className={itemIdentity}>
          {hit.type === 'Contact' && (
            <span className={itemType} title={hit.company}>
              <Highlight
                attribute="company"
                hit={hit}
                classNames={{ highlighted: hightlight, root: highlightRoot }}
              />
              {(
                hit._highlightResult?.registration_number as
                  | { value?: string }
                  | undefined
              )?.value?.includes('<mark>') &&
                hit.registration_number && (
                  <>
                    {' · '}
                    <Highlight
                      attribute="registration_number"
                      hit={hit}
                      classNames={{
                        highlighted: hightlight,
                        root: highlightRoot
                      }}
                    />
                  </>
                )}
            </span>
          )}
          <div className={itemTitle} title={hit.fullname}>
            <Highlight
              attribute="fullname"
              hit={hit}
              classNames={{ highlighted: hightlight, root: highlightRoot }}
            />
          </div>
          {(['email', 'phone'] as const).map((attr) => {
            const hlValue = (
              hit._highlightResult?.[attr] as { value?: string } | undefined
            )?.value
            if (!hlValue?.includes('<mark>')) return null
            return (
              <div key={attr} className={itemType}>
                <Highlight
                  attribute={attr}
                  hit={hit}
                  classNames={{ highlighted: hightlight, root: highlightRoot }}
                />
              </div>
            )
          })}
        </div>
        {hit.status_name && (
          <Badge
            title={hit.status_name}
            backgroundColor={hit.status_background_color}
            reverse={true}
            maxWidth={150}
            minWidth={70}
          />
        )}
      </div>
    )
  }

  const transformItems: RefinementListProps['transformItems'] = (
    items,
    metadata
  ) => {
    const facetsResults = metadata?.results?._rawResults[0]?.facets?.type ?? {}
    return items.map((item) => ({
      ...item,
      label: `${item.label}${(facetsResults[item.value] ?? 0) > 1 ? 's' : ''}`
    }))
  }

  const handleGlobalShortCut = (e: KeyboardEvent): void => {
    if (e.ctrlKey && e.key === 'k') {
      e.preventDefault()
      setOpen(true)
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', handleGlobalShortCut)
    return (): void => {
      window.removeEventListener('keydown', handleGlobalShortCut)
    }
  })

  if (searchEndpoint === undefined || searchToken === undefined)
    return <Shimmer width={300} height={40} />

  if (
    healthCheck.isError ||
    (healthCheck.data !== undefined && healthCheck.data.status !== 'available')
  )
    return <div>Erreur : recherche indisponible</div>

  const instanceName: string = slug?.replace('.', '-') ?? ''

  return (
    <>
      <div className={searchPlaceholder} onClick={() => setOpen(true)}>
        <FontAwesomeIcon icon={faSearch} />
        Rechercher dans job explorer
        <span className={keysWrapper}>
          <kbd className={keyStyle}>Ctrl</kbd>
          <kbd className={keyStyle}>K</kbd>
        </span>
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        customPositionClass={position}
      >
        <div className={searchBox}>
          {searchClient === null ? (
            <Shimmer width={300} height={40} />
          ) : (
            <InstantSearch
              indexName={`${instanceName}_items:updated_at:desc`}
              searchClient={searchClient as unknown as SearchClient}
            >
              {/* Address-book types (Utilisateur, Externe) share this index but must stay out of global search. */}
              <Configure filters='type = "Candidat" OR type = "Contact"' />
              <SearchBox
                autoFocus={true}
                classNames={{
                  form: searchForm,
                  input: searchInput
                }}
              />
              <NoResultsBoundary fallback={<NoResults />}>
                <EmptyQueryBoundary>
                  <RefinementList
                    attribute="type"
                    classNames={{
                      list: refinementList,
                      label: refinementLabel,
                      labelText: refinementLabelText,
                      checkbox: refinementCheckbox,
                      count: refinementCount,
                      selectedItem: refinementSeleted
                    }}
                    sortBy={['name:asc']}
                    transformItems={transformItems}
                  />
                  <InfiniteHits<Hit<HitProps>>
                    showPrevious={false}
                    hitComponent={CustomHit}
                    classNames={{
                      list: hitList,
                      item: hitItem
                    }}
                    translations={{
                      showMoreButtonText: 'Charger plus de résultats',
                      showPreviousButtonText: 'Charger les résultats précédents'
                    }}
                  />
                </EmptyQueryBoundary>
              </NoResultsBoundary>
            </InstantSearch>
          )}
        </div>
      </Modal>
    </>
  )
}
